import { cn } from '@/shared/utils/cn';
import { CellState, ColumnConfig } from '@/shared/types/table';

/**
 * Genera las clases base para una celda de tabla según su estado
 * Mantiene consistencia en bordes, fondos y transiciones
 *
 * @param state - Estado actual de la celda (default, hover, selected, disabled, focused)
 * @param additionalClasses - Clases adicionales opcionales
 * @param locked - Si la columna está bloqueada (sticky)
 * @param isLastLocked - Si es la última columna bloqueada (para mostrar borde separador)
 * @param isEditing - Si la celda está en modo edición (para mantener color de texto original)
 * @param isRowEditing - Si la fila completa está en modo edición (para aplicar bordes brand)
 * @param isActionsColumn - Si es columna de acciones
 * @param hasFixedWidth - Si la columna tiene width definido; entonces la celda usa overflow-hidden para que el contenido se adapte (wrap/truncate)
 * @returns String con todas las clases CSS combinadas
 */
export const getTableCellClasses = (
  state: CellState = 'default',
  additionalClasses?: string,
  locked?: boolean,
  isLastLocked?: boolean,
  isEditing?: boolean,
  isRowEditing?: boolean,
  isActionsColumn?: boolean,
  hasFixedWidth?: boolean
): string => {
  return cn(
    // Clases base (siempre aplicadas)
    'ui-px-4 ui-py-0 ui-text-sm ui-cursor-pointer',
    'ui-transition-colors ui-duration-150',
    'ui-font-normal',
    'ui-border-b ui-border-b-default',
    'ui-h-[56px]',
    'ui-bg-surface',

    (state === 'selected' || state === 'focused') && !isEditing && !isRowEditing
      ? 'ui-text-brand-selected'
      : 'ui-text-primary',

    // Fondos según estado
    state === 'default' && 'group-hover:ui-bg-surface-hover',
    state === 'hover' && 'ui-bg-surface-hover',
    (state === 'selected' || isRowEditing) && 'ui-bg-surface-brand',
    state === 'disabled' &&
      'ui-bg-surface-disabled ui-opacity-50 ui-cursor-not-allowed !ui-border-b-disabled',
    state === 'focused' && 'ui-bg-surface-brand',

    // Bordes brand superior e inferior cuando la fila está en modo edición usando box-shadow
    isRowEditing &&
      '[box-shadow:inset_0_1px_0_0_var(--ui-color-border-brand),inset_0_-1px_0_0_var(--ui-color-border-brand)]',

    // Celdas bloqueadas son sticky; z-45 para quedar por encima del dropdown en celdas (popoverZIndex 35)
    locked && 'ui-sticky ui-z-[45] ui-isolate',

    // Celdas de acciones sticky a la derecha: z-50 para que SIEMPRE queden por encima del resto de celdas del body al hacer scroll
    isActionsColumn && 'ui-sticky ui-z-[50] ui-isolate ui-border-l ui-border-l-default',

    // Borde derecho para la última columna bloqueada
    isLastLocked && 'ui-border-r-2 ui-border-r-default',

    // Con ancho fijo: impedir que el contenido desborde; que se adapte (wrap/truncate) dentro de la celda
    hasFixedWidth && 'ui-overflow-hidden ui-min-w-0',

    // Clases adicionales
    additionalClasses
  );
};

/** Ancho por defecto de la columna de acciones cuando no tiene width definido (evita que se colapse). */
export const DEFAULT_ACTIONS_COLUMN_WIDTH = 120;

/** Ancho mínimo por defecto para columnas sin width definido (ancho automático). Evita que se compriman y que se superpongan a la columna de acciones; fuerza scroll horizontal cuando el viewport es pequeño. */
export const DEFAULT_MIN_COLUMN_WIDTH = 80;

/** Ancho mínimo de la columna flex (maxWidth: 'auto' sin width). Es la que toma el espacio restante y puede truncar; con este mínimo se evita que quede demasiado estrecha. */
export const DEFAULT_FLEX_COLUMN_MIN_WIDTH = 300;

/**
 * Genera las propiedades comunes para una celda (data-attributes y estilos)
 * Mantiene consistencia en anchos y identificadores
 *
 * @param column - Configuración de la columna
 * @param stickyLeftOffset - Offset izquierdo para columnas bloqueadas (default: 0)
 * @returns Objeto con data-attributes y estilos para la celda
 */
export const getTableCellProps = (
  column: ColumnConfig,
  stickyLeft: number = 0,
  stickyRight?: number,
  isActionsColumn?: boolean,
  hasColumnMaxWidthAuto?: boolean
) => {
  const hasWidth = column.width !== undefined;
  const isActionsNoWidth = isActionsColumn && !hasWidth;

  // Columna de acciones sin width: ancho explícito para que no se colapse.
  const effectiveMinForAuto = column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH;
  const effectiveActionsWidth = column.minWidth ?? DEFAULT_ACTIONS_COLUMN_WIDTH;

  let width: number | string | undefined;
  let minWidth: number;

  if (isActionsNoWidth) {
    width = effectiveActionsWidth;
    minWidth = effectiveActionsWidth;
  } else if (hasColumnMaxWidthAuto) {
    if (
      column.maxWidth === 'auto' &&
      (column.width === undefined || typeof column.width !== 'number')
    ) {
      width = undefined;
      minWidth = column.minWidth ?? DEFAULT_FLEX_COLUMN_MIN_WIDTH;
    } else {
      width =
        typeof column.width === 'number' || typeof column.width === 'string'
          ? column.width
          : effectiveMinForAuto;
      minWidth = column.minWidth ?? (column.width !== undefined ? 0 : DEFAULT_MIN_COLUMN_WIDTH);
    }
  } else {
    // Sin hasColumnMaxWidthAuto: tabla con width:max-content. Para columnas SIN width,
    // los navegadores suelen ignorar solo min-width en celdas; hay que dar width explícito.
    if (
      column.maxWidth === 'auto' &&
      (column.width === undefined || typeof column.width !== 'number')
    ) {
      width = '100%';
      minWidth = column.minWidth ?? 0;
    } else if (hasWidth) {
      width = column.width;
      minWidth = column.minWidth ?? 0;
    } else {
      // Columna con ancho automático: forzar width Y minWidth al mismo valor para que la tabla no colapse y aparezca scroll.
      width = effectiveMinForAuto;
      minWidth = effectiveMinForAuto;
    }
  }

  const resolvedWidth =
    typeof column.width === 'number'
      ? column.width
      : typeof column.width === 'string'
        ? parseFloat(column.width)
        : NaN;
  const defaultActionsWidth = column.minWidth ?? DEFAULT_ACTIONS_COLUMN_WIDTH;
  const maxWidth = isActionsNoWidth
    ? defaultActionsWidth
    : column.maxWidth === 'auto'
      ? undefined
      : (column.maxWidth ?? (Number.isFinite(resolvedWidth) ? resolvedWidth : undefined));

  return {
    'data-column-id': column.id,
    style: {
      width,
      minWidth,
      maxWidth,
      ...(column.locked && { left: `var(--table-sticky-left-${column.id}, ${stickyLeft}px)` }),
      ...(isActionsColumn && {
        right: `var(--table-sticky-right-${column.id}, ${stickyRight ?? 0}px)`,
      }),
    },
  };
};

/**
 * Indica si la columna es la "flex" (maxWidth: 'auto' sin width): toma el espacio restante y debe poder truncar con ellipsis.
 */
export const isFlexColumn = (column: ColumnConfig, hasColumnMaxWidthAuto?: boolean): boolean =>
  Boolean(
    hasColumnMaxWidthAuto &&
    column.maxWidth === 'auto' &&
    (column.width === undefined || typeof column.width !== 'number')
  );

/**
 * Devuelve el ancho mínimo efectivo en píxeles de una columna (para calcular el minWidth total de la tabla).
 */
export const getColumnMinWidthPx = (
  column: ColumnConfig,
  isActionsColumn?: boolean,
  hasColumnMaxWidthAuto?: boolean
): number => {
  const hasWidth = column.width !== undefined;
  const isActionsNoWidth = isActionsColumn && !hasWidth;
  const effectiveMin = column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH;
  const effectiveActions = column.minWidth ?? DEFAULT_ACTIONS_COLUMN_WIDTH;

  if (isActionsNoWidth) return effectiveActions;
  if (hasColumnMaxWidthAuto) {
    if (
      column.maxWidth === 'auto' &&
      (column.width === undefined || typeof column.width !== 'number')
    ) {
      return column.minWidth ?? DEFAULT_FLEX_COLUMN_MIN_WIDTH;
    }
    if (column.width !== undefined) return column.minWidth ?? 0;
    return DEFAULT_MIN_COLUMN_WIDTH;
  }
  if (
    column.maxWidth === 'auto' &&
    (column.width === undefined || typeof column.width !== 'number')
  )
    return column.minWidth ?? 0;
  if (hasWidth) return column.minWidth ?? 0;
  return effectiveMin;
};

/**
 * Genera las clases de alineamiento del contenido de una celda
 *
 * @param contentAlign - Alineamiento del contenido ('left' | 'right' | 'center')
 * @returns String con la clase de alineamiento correspondiente
 */
export const getContentAlignClass = (
  contentAlign: 'left' | 'right' | 'center' = 'left'
): string => {
  switch (contentAlign) {
    case 'right':
      return 'ui-justify-end';
    case 'center':
      return 'ui-justify-center';
    case 'left':
    default:
      return 'ui-justify-start';
  }
};

/**
 * Handler genérico para el click de una celda
 * Ejecuta el callback de focus sin detener la propagación
 * para permitir que onRowClick funcione correctamente
 */
export const handleCellClick = (_e: React.MouseEvent, onCellFocus?: () => void) => {
  onCellFocus?.();
};
